import { Spinner } from '@chakra-ui/react'
import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

const REDIRECT_DELAY_MS = 1000

/**
 * Post-signup URL for conversion tracking (e.g. Brave Ads).
 * The user hits this URL after completing signup; we redirect after a short delay
 * so the conversion URL is visible and the page doesn’t feel like an instant flash.
 *
 * Where we redirect TO depends on whether auto-login seeded an Auth0 session
 * during signup (see attemptAutoLoginAfterSignup):
 *  - seeded  → hard-navigate to /app so the Auth0 SDK re-initialises on a fresh
 *    load and adopts the seeded session (a client-side route would not re-run the
 *    SDK's session check).
 *  - not seeded → fall back to the standard Universal Login redirect at /login.
 */
export default function SignupCompleteScreen() {
  const history = useHistory()
  const location = useLocation<{ autoLoggedIn?: boolean } | undefined>()
  const autoLoggedIn = location.state?.autoLoggedIn ?? false

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (autoLoggedIn) {
        window.location.assign('/app')
      } else {
        history.replace('/login')
      }
    }, REDIRECT_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [history, autoLoggedIn])

  return (
    <div className="bg-bg-base dark:bg-brand-dark-background flex min-h-screen items-center justify-center text-fg-base">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="xl" />
        <p className="text-description">
          {autoLoggedIn ? 'Signing you in…' : 'Redirecting to login…'}
        </p>
      </div>
    </div>
  )
}
